import { useEffect, useId, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

interface DangerConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmSlug: string;
  actionLabel: string;
  pendingLabel?: string;
  auditTrailText?: string;
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
}

export function DangerConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmSlug,
  actionLabel,
  pendingLabel,
  auditTrailText = "This action will be logged.",
  onConfirm,
  isPending = false,
}: DangerConfirmDialogProps) {
  const [confirmText, setConfirmText] = useState("");
  const [understood, setUnderstood] = useState(false);
  const inputId = useId();
  const checkboxId = useId();

  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setUnderstood(false);
    }
  }, [open]);

  const slugMatches =
    !!confirmSlug &&
    confirmText.trim().toLowerCase() === confirmSlug.toLowerCase();
  const canConfirm = slugMatches && understood && !isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle
              className="h-5 w-5 text-[color:var(--color-danger)]"
              aria-hidden
            />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="typo-body text-[color:var(--color-text-md)]">
                {description}
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor={inputId}
                  className="typo-label text-[color:var(--color-text-lo)]"
                >
                  Type{" "}
                  <code className="px-1 py-0.5 rounded bg-[color:var(--color-surface-2)] text-xs">
                    {confirmSlug}
                  </code>{" "}
                  to confirm
                </label>
                <Input
                  id={inputId}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={confirmSlug}
                  autoComplete="off"
                  disabled={isPending}
                />
              </div>
              <label
                htmlFor={checkboxId}
                className="flex items-start gap-2 typo-body text-[color:var(--color-text-md)] cursor-pointer"
              >
                <Checkbox
                  id={checkboxId}
                  checked={understood}
                  onCheckedChange={(v) => setUnderstood(v === true)}
                  disabled={isPending}
                  className="mt-0.5"
                />
                <span>I understand this is irreversible.</span>
              </label>
              <p className="typo-label text-[color:var(--color-text-lo)]">
                {auditTrailText}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/90 disabled:opacity-50 disabled:pointer-events-none"
            disabled={!canConfirm}
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                {pendingLabel ?? `${actionLabel}…`}
              </>
            ) : (
              actionLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
